import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, Buton, cn, Kart } from "@koclukpanel/ui";
import { useSudoKapisi, useSudoOnce } from "./SudoKapisi";
import { TekKayitKurtarma } from "./TekKayitKurtarma";
import { TekTabloKurtarma } from "./TekTabloKurtarma";
import {
  ApiHatasi,
  geriYuklemeCalistir,
  geriYuklemeGecmisi,
  geriYuklemeTaslagi,
  yedekKonsolu,
  type GeriYuklemeKapsami,
  type GeriYuklemeTaslagi,
  type HedefOrtam,
} from "./api";

/**
 * GERI YUKLEME SIHIRBAZI — F-YED-23…29
 *
 * ================================================================
 * EKRAN KAPI DEGIL, KAPININ GORUNTUSU
 * ================================================================
 * Buradaki hicbir kontrol koruma saymiyor: sunucu ayni kapilari
 * kendisi geciriyor (`geri-yukleme-kurallari.ts`). Ekranin isi
 * kullaniciyi ne yapmak uzere oldugu konusunda YANILTMAMAK.
 *
 * O yuzden:
 *   · varsayilan hedef SUNUCUDAN geliyor, ekran kendi tutmuyor
 *   · onay metni SUNUCUDAN geliyor
 *   · etki cumlesi SUNUCUDAN geliyor — "?" gorunuyorsa oyle kalıyor
 */

const KAPSAM_ADI: Record<GeriYuklemeKapsami, string> = {
  FULL_SYSTEM: "Tam sistem",
  DATABASE_ONLY: "Yalnızca veritabanı",
  SINGLE_TABLE: "Tek tablo",
  SINGLE_RECORD: "Tek kayıt",
  POINT_IN_TIME: "Belirli ana dönüş",
};

const KAPSAM_ACIKLAMASI: Record<GeriYuklemeKapsami, string> = {
  FULL_SYSTEM: "Veritabanı, dosyalar ve yapılandırma birlikte.",
  DATABASE_ONLY: "Yalnızca veritabanı; yüklenen dosyalara dokunulmaz.",
  SINGLE_TABLE: "Tek bir tablo. Kırılacak bağlantılar önce listelenir.",
  SINGLE_RECORD: "“Şu öğrencinin silinen programını getir.” Farkı gösterilir, onaylanırsa yazılır.",
  POINT_IN_TIME: "“Dün saat 14:20'ye dön.” Yanlışlıkla yapılan toplu silmede tek çare budur.",
};

function tarih(iso: string | null): string {
  if (iso === null) return "—";
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function GeriYukleme() {
  const qc = useQueryClient();
  const [kapsam, setKapsam] = useState<GeriYuklemeKapsami>("DATABASE_ONLY");
  const [hedef, setHedef] = useState<HedefOrtam>("SANDBOX");
  const [runId, setRunId] = useState<string>("");
  const [an, setAn] = useState<string>("");
  const [tablo, setTablo] = useState<string>("");
  const [kayitId, setKayitId] = useState<string>("");
  const [taslak, setTaslak] = useState<GeriYuklemeTaslagi | null>(null);
  const [onay, setOnay] = useState("");
  const { hataYakala, kapi, sudoSoruluyor, sorguKapisi } = useSudoKapisi(
    "Geri yükleme mevcut veriyi ezer ve geri alınamaz. Devam etmeden önce şifrenizi doğrulayın.",
  );

  // Pencere kapaliysa sifre ONCE sorulur, gecmis sorgusu 403 atmaz (UI/UX Dalga 2, M6).
  const { sudoHazir, onKapi } = useSudoOnce(
    "Geri yükleme mevcut veriyi ezer ve geri alınamaz. Devam etmeden önce şifrenizi doğrulayın.",
  );

  const konsol = useQuery({ queryKey: ["yedek-konsolu"], queryFn: yedekKonsolu });
  const gecmis = useQuery({
    queryKey: ["geri-yukleme-gecmis"],
    queryFn: geriYuklemeGecmisi,
    // Sudo penceresi kapaliysa 403 doner; tekrar denemek ayni sonucu
    // verir, ekranin sormasi gerekir.
    retry: false,
    enabled: sudoHazir,
  });

  const taslakOlustur = useMutation({
    mutationFn: () =>
      geriYuklemeTaslagi({
        kapsam,
        hedef,
        runId: kapsam === "POINT_IN_TIME" ? null : runId || null,
        pointInTime: kapsam === "POINT_IN_TIME" && an ? new Date(an).toISOString() : null,
        tableName: tablo || null,
        recordId: kayitId || null,
      }),
    onSuccess: (t) => {
      setTaslak(t);
      setOnay("");
    },
    onError: hataYakala(() => taslakOlustur.mutate()),
  });

  const calistir = useMutation({
    mutationFn: (t: GeriYuklemeTaslagi) => geriYuklemeCalistir(t.id, onay),
    onSuccess: () => {
      setTaslak(null);
      setOnay("");
      void qc.invalidateQueries({ queryKey: ["geri-yukleme-gecmis"] });
      void qc.invalidateQueries({ queryKey: ["yedek-konsolu"] });
    },
    onError: hataYakala(() => {
      if (taslak !== null) calistir.mutate(taslak);
    }),
  });

  const kosular = (konsol.data?.kosular ?? []).filter(
    (k) => k.durum === "SUCCEEDED" && !k.suresiDoldu,
  );

  /*
    ================================================================
    GECMIS LISTESI DE SUDO ISTIYOR — VE EKRAN SORMUYORDU
    ================================================================
    `sistem.yedek.geriyukle` sudo listesinde ve `GET
    /yedek/geri-yukleme/gecmis` o izni soruyor. Kanca bu ekranda
    ZATEN kuruluydu — ama yalnizca YAZMA islemleri icin (`hataYakala`).
    Sudo penceresi kapaliyken ekran aciliyor, gecmis listesi 403
    donuyor ve kullaniciya sifre soracak hicbir sey cizilmiyordu.

    `Entegrasyonlar` (06.08.2026), `MesajDenetim` (07.09.2026) ve bu:
    ucu de ayni sekil — kanca cagriliyor, YAZMAYA bagli, OKUMA acikta.
    Bu yuzden bekcinin listesi artik kaynaktan turetiliyor
    (`sudo-okuma-kapisi.test.ts`).
  */
  if (onKapi) return <>{onKapi}</>;
  if (gecmis.isError) {
    const kapiEkrani = sorguKapisi(gecmis.error, () => void gecmis.refetch());
    if (kapiEkrani) return <>{kapiEkrani}</>;
  }

  return (
    <div className="flex flex-col gap-6">
      {kapi}

      {/* ---------- 1. adım: NE? ---------- */}
      <Adim no={1} baslik="Ne geri yüklenecek?">
        <div className="flex flex-col gap-2">
          {(Object.keys(KAPSAM_ADI) as GeriYuklemeKapsami[]).map((k) => (
            <label
              key={k}
              className={cn(
                "rounded-alan flex cursor-pointer gap-3 border p-3",
                kapsam === k ? "border-marka-600 bg-marka-50" : "border-notr-200 bg-notr-0",
              )}
            >
              <input
                type="radio"
                name="kapsam"
                checked={kapsam === k}
                onChange={() => {
                  setKapsam(k);
                  setTaslak(null);
                }}
                className="mt-1"
              />
              <span className="min-w-0">
                <span className="text-notr-900 block text-sm font-medium">{KAPSAM_ADI[k]}</span>
                <span className="text-notr-600 block text-xs">{KAPSAM_ACIKLAMASI[k]}</span>
              </span>
            </label>
          ))}
        </div>

        {(kapsam === "SINGLE_TABLE" || kapsam === "SINGLE_RECORD") && (
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <div className="flex-1">
              <Alan etiket="Tablo adı" value={tablo} onChange={(e) => setTablo(e.target.value)} />
            </div>
            {kapsam === "SINGLE_RECORD" && (
              <div className="flex-1">
                <Alan
                  etiket="Kayıt kimliği"
                  value={kayitId}
                  onChange={(e) => setKayitId(e.target.value)}
                />
              </div>
            )}
          </div>
        )}
      </Adim>

      {/* ---------- 2. adım: NEREDEN? ---------- */}
      <Adim no={2} baslik="Hangi yedekten?">
        {kapsam === "POINT_IN_TIME" ? (
          <Alan
            etiket="Hedef an (dakika hassasiyeti)"
            type="datetime-local"
            value={an}
            onChange={(e) => {
              setAn(e.target.value);
              setTaslak(null);
            }}
          />
        ) : kosular.length === 0 ? (
          /*
            Kullanilabilir yedek yoksa SOYLENIYOR. Bos bir liste
            "secim yapmadin" gibi okunur, oysa sorun sececek bir sey
            olmamasi.
          */
          <p className="rounded-alan bg-notr-50 text-notr-600 p-3 text-sm">
            Geri yüklenebilecek yedek yok. Yalnızca <strong>başarılı</strong> ve saklama süresi
            dolmamış koşular listelenir.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {kosular.map((k) => (
              <label
                key={k.id}
                className={cn(
                  "rounded-alan flex cursor-pointer items-start gap-3 border p-3",
                  runId === k.id ? "border-marka-600 bg-marka-50" : "border-notr-200 bg-notr-0",
                )}
              >
                <input
                  type="radio"
                  name="kosu"
                  checked={runId === k.id}
                  onChange={() => {
                    setRunId(k.id);
                    setTaslak(null);
                  }}
                  className="mt-1"
                />
                <span className="min-w-0">
                  <span className="text-notr-900 block text-sm">{tarih(k.basladi)}</span>
                  <span className="text-notr-600 block text-xs">
                    {k.hedefAdi}
                    {/*
                      Dogrulanmamis yedek BURADA da isaretli: secim
                      aninda gorulmezse, geri yuklenemeyecek bir yedek
                      secilir ve bu ancak calistirma aninda anlasilir.
                    */}
                    {k.dogrulandiMi === null && " · doğrulanmadı"}
                    {k.dogrulandiMi === false && " · DOĞRULAMA BAŞARISIZ"}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}
      </Adim>

      {/* ---------- 3. adım: NEREYE? ---------- */}
      <Adim no={3} baslik="Nereye?">
        <div className="flex flex-col gap-2">
          <label
            className={cn(
              "rounded-alan flex cursor-pointer gap-3 border p-3",
              hedef === "SANDBOX" ? "border-marka-600 bg-marka-50" : "border-notr-200 bg-notr-0",
            )}
          >
            <input
              type="radio"
              name="hedef"
              checked={hedef === "SANDBOX"}
              onChange={() => {
                setHedef("SANDBOX");
                setTaslak(null);
              }}
              className="mt-1"
            />
            <span>
              <span className="text-notr-900 block text-sm font-medium">
                Geçici doğrulama ortamı <span className="text-xs font-normal">(varsayılan)</span>
              </span>
              <span className="text-notr-600 block text-xs">
                Üretime dokunmaz. Yedek ayrı bir veritabanına açılır ve ölçülür.
              </span>
            </span>
          </label>

          <label
            className={cn(
              "rounded-alan flex cursor-pointer gap-3 border p-3",
              hedef === "PRODUCTION"
                ? "border-hata-500 bg-hata-500/10"
                : "border-notr-200 bg-notr-0",
            )}
          >
            <input
              type="radio"
              name="hedef"
              checked={hedef === "PRODUCTION"}
              onChange={() => {
                setHedef("PRODUCTION");
                setTaslak(null);
              }}
              className="mt-1"
            />
            <span>
              <span className="text-notr-900 block text-sm font-medium">Üretim</span>
              {/*
                Kapali oldugu SECIM ANINDA yaziyor, calistirmada
                degil. Alti adim doldurup en sonda "bu kapali"
                demek, kullanicinin zamanini bosa harcamak olurdu.
              */}
              <span className="text-hata-500 block text-xs">
                Şu anda devrede değil. Bakım modu ve geri alma adımları tamamlanmadan açılmayacak —
                yarısında kalan bir üretim geri yüklemesi, hiç başlamamış olmaktan çok daha kötüdür.
              </span>
            </span>
          </label>
        </div>
      </Adim>

      {/* ---------- 4. adım: ETKİ ---------- */}
      <Adim no={4} baslik="Etki">
        {taslak === null ? (
          <div className="flex flex-col gap-2">
            <p className="text-notr-600 text-sm">
              Etkiyi görmek için hesaplayın. <strong>Onay adımı bundan önce açılmaz</strong> — kaç
              satırın kaybolacağını görmeden verilen onay, onay değildir.
            </p>
            <div>
              <Buton onClick={() => taslakOlustur.mutate()} disabled={taslakOlustur.isPending}>
                {taslakOlustur.isPending ? "Hesaplanıyor…" : "Etkiyi hesapla"}
              </Buton>
            </div>
            {taslakOlustur.isError && !sudoSoruluyor && (
              <p className="text-hata-500 text-xs">
                {taslakOlustur.error instanceof ApiHatasi
                  ? taslakOlustur.error.message
                  : "Etki hesaplanamadı."}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="rounded-alan bg-notr-50 text-notr-800 p-3 text-sm">
              {taslak.etkiCumlesi}
            </p>
            {taslak.oncekiYedekGerekli && (
              <p className="text-notr-600 text-xs">
                Üretime yazmadan önce mevcut durumun anlık yedeği alınır{/* F-YED-25 */}.
              </p>
            )}
          </div>
        )}
      </Adim>

      {/* ---------- 5–6. adım: ONAY + ÇALIŞTIR ---------- */}
      <Adim no={5} baslik="Onay ve çalıştırma">
        {taslak === null ? (
          <p className="text-notr-600 text-sm">Önce etkiyi hesaplayın.</p>
        ) : (
          <div className="flex flex-col gap-3">
            <Alan
              etiket={`Onaylamak için ${taslak.onayMetni} yazın`}
              value={onay}
              onChange={(e) => setOnay(e.target.value)}
              placeholder={taslak.onayMetni}
            />

            <div>
              <Buton
                onClick={() => calistir.mutate(taslak)}
                disabled={calistir.isPending || onay.trim().length === 0}
              >
                {calistir.isPending ? "Çalıştırılıyor…" : "Geri yüklemeyi başlat"}
              </Buton>
            </div>

            {/*
              Sunucunun gerekcesi OLDUGU GIBI gosteriliyor. Ekran
              kendi metnini uydursaydi, sunucu yeni bir kapi
              ekledigi gun kullanici "neden reddedildi" sorusunun
              cevabini goremezdi.
            */}
            {calistir.isError && !sudoSoruluyor && <RedKutusu hata={calistir.error} />}
            {calistir.isSuccess && (
              <p className="text-onay-700 text-xs">Geri yükleme tamamlandı.</p>
            )}
          </div>
        )}
      </Adim>

      {/*
        Sihirbazin ADIMLARINDAN sonra, gecmisten once. Ayri bir is:
        sihirbaz "butun sistemi geri yukle" akisi, bu ise tek kaydi
        getiriyor ve iki adimda calisiyor (onizle, yaz).
      */}
      <TekKayitKurtarma runId={runId} />
      {/*
        TEK TABLO, TEK KAYDIN ALTINDA: ikisi ayni yedekten calisiyor
        ve sirasi bilincli — once daha az yikici olan. Ayri bir ekran
        olsaydi, kayit kurtarmayi deneyip yetmedigini goren kisi tablo
        secenegini aramak zorunda kalirdi.
      */}
      <TekTabloKurtarma runId={runId} />

      <section className="flex flex-col gap-2">
        <h3 className="text-notr-900 text-sm font-semibold">Geçmiş</h3>
        {gecmis.data === undefined || gecmis.data.length === 0 ? (
          <p className="text-notr-600 text-sm">Henüz geri yükleme yapılmamış.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {gecmis.data.map((g) => (
              <Kart key={g.id} olarak="li" className="p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-notr-900">
                    {KAPSAM_ADI[g.scope]} → {g.targetEnv === "SANDBOX" ? "geçici ortam" : "üretim"}
                  </span>
                  <span className="text-notr-600 text-xs">{g.status}</span>
                </div>
                <p className="text-notr-600 mt-0.5 text-xs">
                  {g.requester.firstName} {g.requester.lastName} · {tarih(g.createdAt)}
                  {g.durationSec !== null && ` · ${g.durationSec} sn`}
                </p>
                {g.error !== null && <p className="text-hata-500 mt-1 text-xs">{g.error}</p>}
              </Kart>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function RedKutusu({ hata }: { hata: unknown }) {
  if (!(hata instanceof ApiHatasi)) {
    return <p className="text-hata-500 text-xs">Geri yükleme başlatılamadı.</p>;
  }
  return (
    <div className="rounded-alan bg-hata-500/10 text-notr-800 p-3 text-xs">
      <p className="text-hata-500 font-medium">{hata.message}</p>
      {hata.ayrinti && (
        <ul className="mt-1 list-disc pl-4">
          {Object.entries(hata.ayrinti).map(([k, v]) => (
            <li key={k}>{v}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Adim({ no, baslik, children }: { no: number; baslik: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-notr-900 text-sm font-semibold">
        <span className="bg-notr-200 mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full text-xs">
          {no}
        </span>
        {baslik}
      </h3>
      {children}
    </section>
  );
}
